const COMPANY_NAME: string = import.meta.env.VITE_COMPANY_NAME || "";
const ADDRESS: string = import.meta.env.VITE_COMPANY_ADDRESS || "";
// Vipul Agora building. Until the office has its own Google listing, searching
// its name or address makes Google show a neighbouring business, with that
// business's name and rating, so the pin uses coordinates instead.
const OFFICE_COORDINATES = "28.479608,77.085793";

// Google Business Profile values. Each part stays hidden until its value is set,
// so the site never shows a rating or review count that isn't real.
const GMB_URL: string | undefined = import.meta.env.VITE_GMB_URL;
const GMB_RATING = Number(import.meta.env.VITE_GMB_RATING);
const GMB_REVIEW_COUNT = Number(import.meta.env.VITE_GMB_REVIEW_COUNT);

const MAP_QUERY = GMB_URL ? encodeURIComponent(`${COMPANY_NAME}, ${ADDRESS}`) : OFFICE_COORDINATES;
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${MAP_QUERY}`;

export const OfficeMap = ({ height = 350 }: { height?: number }) => (
  <div className="relative">
    <iframe
      title={`${COMPANY_NAME} office location`}
      src={`https://www.google.com/maps?q=${MAP_QUERY}&z=17&output=embed`}
      width="100%"
      height={height}
      className="border-0 block"
      allowFullScreen
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
    {!GMB_URL && (
      <div className="absolute top-3 left-3 max-w-[75%] bg-white rounded-lg shadow-md px-3 py-2 pointer-events-none">
        <p className="text-[#CC1A1A] font-bold text-sm">{COMPANY_NAME}</p>
        <p className="text-gray-600 text-xs leading-snug">{ADDRESS}</p>
      </div>
    )}
  </div>
);

const GoogleRating = () => {
  if (!GMB_URL || !(GMB_RATING > 0)) return null;
  const fullStars = Math.round(GMB_RATING);

  return (
    <a href={GMB_URL} target="_blank" rel="noopener noreferrer" className="flex flex-wrap items-center gap-2">
      <span className="text-[#1a1a4e] font-bold text-lg">{GMB_RATING.toFixed(1)}</span>
      <span className="text-yellow-400 text-lg" aria-label={`Rated ${GMB_RATING} out of 5 on Google`}>
        {"★".repeat(fullStars)}
        <span className="text-gray-300">{"★".repeat(5 - fullStars)}</span>
      </span>
      {GMB_REVIEW_COUNT > 0 && (
        <span className="text-gray-500 text-sm underline">
          {GMB_REVIEW_COUNT.toLocaleString("en-IN")} Google reviews
        </span>
      )}
    </a>
  );
};

export const OfficeLocationSection = () => {
  return (
    <section className="py-15 bg-[#fdf5f3]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col items-center text-center mb-10">
          <span className="text-[#CC1A1A] text-xs font-black tracking-[0.3em] uppercase mb-3">
            VISIT US
          </span>
          <div className="w-8 h-0.5 bg-[#CC1A1A] mb-5" />
          <h2 className="text-4xl xl:text-5xl font-bold text-[#1a1a4e]">Our Office</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 lg:p-8 flex flex-col gap-4">
            <h3 className="text-[#CC1A1A] font-bold text-xl">{COMPANY_NAME}</h3>
            <p className="text-gray-600 text-base leading-relaxed">{ADDRESS}</p>
            <GoogleRating />
            <div className="flex flex-col gap-2 mt-auto">
              <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" className="text-[#CC1A1A] font-semibold text-base underline hover:text-red-800">
                Get Directions
              </a>
              {GMB_URL && (
                <a href={GMB_URL} target="_blank" rel="noopener noreferrer" className="text-[#CC1A1A] font-semibold text-base underline hover:text-red-800">
                  See Our Google Business Profile
                </a>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-gray-200">
            <OfficeMap />
          </div>
        </div>
      </div>
    </section>
  );
};
